import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';

interface EnhancedAnalyticsConfig {
  enablePerformanceTracking?: boolean;
  enableErrorTracking?: boolean;
  enableUserJourneyTracking?: boolean;
  enableConversionOptimization?: boolean;
}

interface PerformanceMetrics {
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
  ttfb?: number; // Time to First Byte
}

interface LeadScore {
  score: number;
  factors: {
    motivation: number;
    experience: number;
    challenge: number;
    engagement: number;
  };
  tier: 'low' | 'medium' | 'high' | 'premium';
}

export const useEnhancedAnalytics = (config: EnhancedAnalyticsConfig = {}) => {
  const { trackEvent } = useAnalytics();
  const performanceMetrics = useRef<PerformanceMetrics>({});
  const startTime = useRef<number>(Date.now());
  const interactions = useRef<number>(0);
  const lastActivity = useRef<number>(Date.now());

  // Enhanced Performance Tracking
  const trackCoreWebVitals = useCallback(() => {
    if (!config.enablePerformanceTracking) return;

    // Track Core Web Vitals
    if ('web-vital' in window) {
      // LCP - Largest Contentful Paint
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lcp = entries[entries.length - 1];
        performanceMetrics.current.lcp = lcp.startTime;
        
        trackEvent('core_web_vital', {
          metric_name: 'LCP',
          metric_value: lcp.startTime,
          rating: lcp.startTime < 2500 ? 'good' : lcp.startTime < 4000 ? 'needs_improvement' : 'poor',
          event_category: 'performance'
        });
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // FID - First Input Delay
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry: any) => {
          performanceMetrics.current.fid = entry.processingStart - entry.startTime;
          
          trackEvent('core_web_vital', {
            metric_name: 'FID',
            metric_value: entry.processingStart - entry.startTime,
            rating: (entry.processingStart - entry.startTime) < 100 ? 'good' : 
                   (entry.processingStart - entry.startTime) < 300 ? 'needs_improvement' : 'poor',
            event_category: 'performance'
          });
        });
      }).observe({ entryTypes: ['first-input'] });

      // CLS - Cumulative Layout Shift
      new PerformanceObserver((entryList) => {
        let cls = 0;
        entryList.getEntries().forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            cls += entry.value;
          }
        });
        
        performanceMetrics.current.cls = cls;
        
        trackEvent('core_web_vital', {
          metric_name: 'CLS',
          metric_value: cls,
          rating: cls < 0.1 ? 'good' : cls < 0.25 ? 'needs_improvement' : 'poor',
          event_category: 'performance'
        });
      }).observe({ entryTypes: ['layout-shift'] });
    }
  }, [config.enablePerformanceTracking, trackEvent]);

  // Lead Scoring System
  const calculateLeadScore = useCallback((formData: any, timeSpent: number, interactions: number): LeadScore => {
    let score = 0;
    const factors = {
      motivation: 0,
      experience: 0,
      challenge: 0,
      engagement: 0
    };

    // Motivation scoring (0-25 points)
    if (formData.motivation) {
      const motivationScores: Record<string, number> = {
        'lose_weight': 20,
        'build_muscle': 18,
        'improve_health': 22,
        'reduce_stress': 15,
        'increase_productivity': 25,
        'better_sleep': 12,
        'quit_bad_habit': 20,
        'learn_new_skill': 18,
        'other': 10
      };
      factors.motivation = motivationScores[formData.motivation] || 10;
      score += factors.motivation;
    }

    // Experience scoring (0-20 points)
    if (formData.experience) {
      const experienceScores: Record<string, number> = {
        'complete_beginner': 15, // Higher score for beginners (more room for growth)
        'tried_before': 10,
        'some_success': 12,
        'very_experienced': 8
      };
      factors.experience = experienceScores[formData.experience] || 8;
      score += factors.experience;
    }

    // Challenge scoring (0-20 points)
    if (formData.challenge) {
      const challengeScores: Record<string, number> = {
        'lack_motivation': 18,
        'no_time': 15,
        'dont_know_how': 20,
        'lack_accountability': 22, // High value for our accountability app
        'too_overwhelming': 12,
        'other': 8
      };
      factors.challenge = challengeScores[formData.challenge] || 8;
      score += factors.challenge;
    }

    // Engagement scoring (0-35 points)
    const timeScore = Math.min(timeSpent / 60, 10); // Max 10 points for time (1 point per 6 seconds, cap at 1 minute)
    const interactionScore = Math.min(interactions * 2, 15); // 2 points per interaction, cap at 15
    const completionScore = Object.keys(formData).length >= 6 ? 10 : 0; // 10 points for completing most fields
    factors.engagement = timeScore + interactionScore + completionScore;
    score += factors.engagement;

    // Determine tier
    let tier: 'low' | 'medium' | 'high' | 'premium';
    if (score >= 70) tier = 'premium';
    else if (score >= 50) tier = 'high';
    else if (score >= 30) tier = 'medium';
    else tier = 'low';

    return { score, factors, tier };
  }, []);

  // Enhanced Lead Generation Tracking
  const trackEnhancedLead = useCallback((formData: any, source: string, timeSpent: number) => {
    const leadScore = calculateLeadScore(formData, timeSpent, interactions.current);
    
    trackEvent('enhanced_lead_generation', {
      lead_source: source,
      lead_score: leadScore.score,
      lead_tier: leadScore.tier,
      motivation_score: leadScore.factors.motivation,
      experience_score: leadScore.factors.experience,
      challenge_score: leadScore.factors.challenge,
      engagement_score: leadScore.factors.engagement,
      time_spent: timeSpent,
      interactions_count: interactions.current,
      device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page_referrer: document.referrer,
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
      event_category: 'conversion',
      custom_lead_value: leadScore.tier === 'premium' ? 100 : leadScore.tier === 'high' ? 50 : 25
    });
  }, [calculateLeadScore, trackEvent]);

  // Form Abandonment Tracking
  const trackFormAbandonment = useCallback((formData: any, currentField: string, timeSpent: number) => {
    const completedFields = Object.keys(formData).filter(key => formData[key]).length;
    const totalFields = 8; // Adjust based on your form
    const completionPercentage = (completedFields / totalFields) * 100;

    trackEvent('form_abandonment', {
      abandoned_at_field: currentField,
      completion_percentage: completionPercentage,
      completed_fields: completedFields,
      total_fields: totalFields,
      time_spent: timeSpent,
      device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
      event_category: 'form_interaction'
    });
  }, [trackEvent]);

  // Feature Interest Tracking
  const trackFeatureInterest = useCallback((featureName: string, interactionType: 'view' | 'click' | 'hover') => {
    trackEvent('feature_interest', {
      feature_name: featureName,
      interaction_type: interactionType,
      timestamp: Date.now(),
      page_location: window.location.pathname,
      event_category: 'feature_engagement'
    });
  }, [trackEvent]);

  // A/B Test Tracking
  const trackABTestExposure = useCallback((testName: string, variant: string, userId?: string) => {
    trackEvent('ab_test_exposure', {
      test_name: testName,
      variant: variant,
      user_id: userId,
      timestamp: Date.now(),
      page_location: window.location.pathname,
      event_category: 'ab_testing'
    });
  }, [trackEvent]);

  const trackABTestConversion = useCallback((testName: string, variant: string, conversionType: string) => {
    trackEvent('ab_test_conversion', {
      test_name: testName,
      variant: variant,
      conversion_type: conversionType,
      timestamp: Date.now(),
      event_category: 'ab_testing'
    });
  }, [trackEvent]);

  // Real-time User Behavior Tracking
  const trackUserInteraction = useCallback((elementType: string, elementId: string, action: string) => {
    interactions.current += 1;
    lastActivity.current = Date.now();

    trackEvent('user_interaction', {
      element_type: elementType,
      element_id: elementId,
      action: action,
      interaction_count: interactions.current,
      time_since_start: Date.now() - startTime.current,
      event_category: 'user_behavior'
    });
  }, [trackEvent]);

  // Heatmap Data Collection
  const trackHeatmapData = useCallback((x: number, y: number, elementType: string) => {
    trackEvent('heatmap_click', {
      click_x: x,
      click_y: y,
      element_type: elementType,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      scroll_position: window.scrollY,
      event_category: 'heatmap'
    });
  }, [trackEvent]);

  // Error Tracking with Context
  const trackEnhancedError = useCallback((error: Error, errorBoundary?: string, userAction?: string) => {
    trackEvent('enhanced_error', {
      error_message: error.message,
      error_stack: error.stack?.substring(0, 500), // Limit stack trace length
      error_boundary: errorBoundary,
      user_action: userAction,
      page_location: window.location.pathname,
      user_agent: navigator.userAgent,
      timestamp: Date.now(),
      event_category: 'error'
    });
  }, [trackEvent]);

  // Initialize tracking
  useEffect(() => {
    if (config.enablePerformanceTracking) {
      trackCoreWebVitals();
    }

    if (config.enableErrorTracking) {
      const handleError = (event: ErrorEvent) => {
        trackEnhancedError(new Error(event.message), 'global', 'automatic');
      };

      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        trackEnhancedError(new Error(String(event.reason)), 'promise', 'automatic');
      };

      window.addEventListener('error', handleError);
      window.addEventListener('unhandledrejection', handleUnhandledRejection);

      return () => {
        window.removeEventListener('error', handleError);
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      };
    }
  }, [config, trackCoreWebVitals, trackEnhancedError]);

  return {
    trackEnhancedLead,
    trackFormAbandonment,
    trackFeatureInterest,
    trackABTestExposure,
    trackABTestConversion,
    trackUserInteraction,
    trackHeatmapData,
    trackEnhancedError,
    calculateLeadScore,
    getPerformanceMetrics: () => performanceMetrics.current,
    getSessionData: () => ({
      timeSpent: Date.now() - startTime.current,
      interactions: interactions.current,
      lastActivity: lastActivity.current
    })
  };
};